import { Loader2 } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { BackLink, ErrorBanner, getApiErrorMessage, PasswordHint, SignInFooter } from './auth-form-helpers';

interface PersonalSignupFormProps {
  onBack: () => void;
  onSignIn: () => void;
  onSuccess: () => void;
}

export function PersonalSignupForm({ onBack, onSignIn, onSuccess }: PersonalSignupFormProps) {
  const { signupPersonal } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inFlight = useRef(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setError('');
    setIsLoading(true);
    try {
      await signupPersonal({ name, email, password });
      onSuccess();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not create your account.'));
    } finally {
      inFlight.current = false;
      setIsLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <BackLink onClick={onBack} />
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        {error && <ErrorBanner message={error} />}
        <div className="flex flex-col gap-2">
          <Label htmlFor="personal-name">Your name</Label>
          <Input
            id="personal-name"
            ref={nameInputRef}
            required
            value={name}
            autoComplete="name"
            placeholder="Jane Doe"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="personal-email">Email</Label>
          <Input
            id="personal-email"
            required
            type="email"
            value={email}
            autoComplete="email"
            placeholder="you@example.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="personal-password">Password</Label>
          <Input
            id="personal-password"
            required
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
          />
          <PasswordHint />
        </div>
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create personal account
        </Button>
      </form>
      <SignInFooter onSignIn={onSignIn} />
    </div>
  );
}
